import { useMemo, useState } from "react";
import {
  ArrowUpRight,
  Award,
  BarChart3,
  CalendarCheck2,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  FileText,
  Handshake,
  LockKeyhole,
  Mail,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { startLogin } from "@/const";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout, { type DashboardNavItem } from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";

type Tab = "all" | "leads" | "assessments" | "blueprints";
type LeadSource = "websiteSubmission" | "inquiry";
type LeadStatus = "new" | "reviewed" | "closed";
type BlueprintStatus = "queued" | "awaiting_payment" | "in_progress" | "delivered" | "canceled" | "failed";
type AssessmentStatus = "confirmed" | "completed" | "canceled" | "failed";

type DashboardItem = {
  key: string;
  category: "lead" | "assessment" | "blueprint";
  source: LeadSource | "assessment" | "blueprint";
  id: number;
  label: string;
  person: string;
  email: string;
  phone?: string | null;
  business?: string | null;
  createdAt: Date;
  status: LeadStatus | AssessmentStatus | BlueprintStatus;
  paymentStatus?: string;
  amountCents?: number;
  message?: string | null;
  reference?: string;
  details: Record<string, unknown>;
};

const leadStatuses: LeadStatus[] = ["new", "reviewed", "closed"];
const assessmentStatuses: AssessmentStatus[] = ["confirmed", "completed", "canceled", "failed"];
const blueprintStatuses: BlueprintStatus[] = ["queued", "awaiting_payment", "in_progress", "delivered", "canceled", "failed"];

const labels: Record<string, string> = {
  contact: "Consultation request",
  blueprint: "Free Blueprint request",
  fitlife: "FitLife-X subscriber",
  client_application: "Client application",
  coach_application: "Coach application",
  vendor_application: "Vendor partner application",
  contact_lead: "Contact lead",
  consultation: "Consultation lead",
  blueprint_lead: "Legacy Blueprint lead",
};

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, letter => letter.toUpperCase());
}

function parseDetails(value: string) {
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function nameFor(firstName: string | null, lastName: string | null, fallback: string) {
  const name = [firstName, lastName].filter(Boolean).join(" ");
  return name || fallback;
}

function StatusPill({ status }: { status: string }) {
  return <span className={`ops-status ops-status--${status}`}>{humanize(status)}</span>;
}

function DetailsPanel({ item, onClose, onStatusChange, updating }: {
  item: DashboardItem;
  onClose: () => void;
  onStatusChange: (status: string) => void;
  updating: boolean;
}) {
  const statuses = item.category === "lead" ? leadStatuses : item.category === "assessment" ? assessmentStatuses : blueprintStatuses;
  const details = Object.entries(item.details).filter(([, value]) => value !== "" && value !== null && value !== undefined);

  return <div className="ops-drawer" role="dialog" aria-modal="true" aria-labelledby="ops-detail-title">
    <button type="button" className="ops-drawer__scrim" aria-label="Close detail panel" onClick={onClose} />
    <aside className="ops-drawer__panel">
      <button type="button" className="ops-drawer__close" aria-label="Close detail panel" onClick={onClose}><X size={18} /></button>
      <div className="ops-drawer__heading"><span className="ops-record-icon">{item.category === "blueprint" ? <ClipboardList size={18} /> : item.category === "assessment" ? <CalendarCheck2 size={18} /> : <UsersRound size={18} />}</span><div><p className="ops-overline">{item.label}</p><h2 id="ops-detail-title">{item.person}</h2></div></div>
      <div className="ops-drawer__status"><StatusPill status={item.status} /><span>Received {item.createdAt.toLocaleString()}</span></div>
      <div className="ops-contact-actions"><a href={`mailto:${item.email}`}><Mail size={15} />Email</a>{item.phone && <a href={`tel:${item.phone}`}><Phone size={15} />Call</a>}</div>
      <dl className="ops-detail-list"><div><dt>Email</dt><dd><a href={`mailto:${item.email}`}>{item.email}</a></dd></div>{item.phone && <div><dt>Phone</dt><dd><a href={`tel:${item.phone}`}>{item.phone}</a></dd></div>}{item.business && <div><dt>Business</dt><dd>{item.business}</dd></div>}{item.reference && <div><dt>Reference</dt><dd>{item.reference}</dd></div>}{item.amountCents !== undefined && <div><dt>Value</dt><dd>{item.amountCents ? `$${(item.amountCents / 100).toLocaleString()} USD` : "Free diagnostic"}</dd></div>}{item.paymentStatus && <div><dt>Payment</dt><dd>{humanize(item.paymentStatus)}</dd></div>}</dl>
      {item.message && <section className="ops-detail-message"><p>Message</p><blockquote>{item.message}</blockquote></section>}
      <section className="ops-detail-intake"><p>Submission details</p>{details.length ? <dl>{details.map(([key, value]) => <div key={key}><dt>{humanize(key)}</dt><dd>{typeof value === "object" ? JSON.stringify(value) : String(value)}</dd></div>)}</dl> : <span>No additional form data was captured.</span>}</section>
      <label className="ops-status-control"><span>{item.category === "lead" ? "Lead status" : item.category === "assessment" ? "Assessment status" : "Fulfillment status"}</span><select value={item.status} onChange={event => onStatusChange(event.target.value)} disabled={updating}>{statuses.map(status => <option value={status} key={status}>{humanize(status)}</option>)}</select></label>
    </aside>
  </div>;
}

export default function OperationsDashboard() {
  const { user, loading } = useAuth();
  const utils = trpc.useUtils();
  const dashboard = trpc.operations.dashboardData.useQuery(undefined, { enabled: user?.role === "admin" });
  const leadStatusMutation = trpc.operations.updateLeadStatus.useMutation({
    onSuccess: () => { void utils.operations.dashboardData.invalidate(); toast.success("Lead status updated."); },
    onError: error => toast.error("Could not update the lead.", { description: error.message }),
  });
  const blueprintStatusMutation = trpc.blueprints.setFulfillmentStatus.useMutation({
    onSuccess: () => { void utils.operations.dashboardData.invalidate(); toast.success("Blueprint status updated."); },
    onError: error => toast.error("Could not update the Blueprint.", { description: error.message }),
  });
  const assessmentStatusMutation = trpc.operations.updateAssessmentStatus.useMutation({
    onSuccess: () => { void utils.operations.dashboardData.invalidate(); toast.success("Assessment status updated."); },
    onError: error => toast.error("Could not update the assessment.", { description: error.message }),
  });
  const [tab, setTab] = useState<Tab>("all");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState<DashboardItem | null>(null);

  const navItems: DashboardNavItem[] = [
    { icon: BarChart3, label: "Overview", path: "/operations" },
    { icon: UsersRound, label: "Lead inbox", path: "/operations", anchor: "#lead-inbox" },
    { icon: CalendarCheck2, label: "Assessments", path: "/operations", anchor: "#assessment-requests" },
    { icon: ClipboardList, label: "Blueprints", path: "/operations", anchor: "#blueprint-requests" },
    { icon: Handshake, label: "Vendors & deals", path: "/operations/vendors" },
    { icon: Award, label: "Coach network", path: "/operations/coaches" },
    // These two pages existed but were reachable only by typing the URL.
    { icon: ClipboardList, label: "Blueprint desk", path: "/operations/blueprints" },
    { icon: ShieldCheck, label: "Audit log", path: "/operations/audit" },
  ];

  const records = useMemo<DashboardItem[]>(() => {
    if (!dashboard.data) return [];
    const currentForms = dashboard.data.websiteSubmissions.map(submission => ({
      key: `website-${submission.id}`,
      category: "lead" as const,
      source: "websiteSubmission" as const,
      id: submission.id,
      label: labels[submission.formType] ?? humanize(submission.formType),
      person: nameFor(submission.firstName, submission.lastName, submission.email),
      email: submission.email,
      phone: submission.phone,
      business: submission.businessName,
      createdAt: submission.createdAt,
      status: submission.status,
      message: submission.message,
      details: parseDetails(submission.payload),
    }));
    const legacyLeads = dashboard.data.inquiries.map(inquiry => ({
      key: `inquiry-${inquiry.id}`,
      category: "lead" as const,
      source: "inquiry" as const,
      id: inquiry.id,
      label: labels[`${inquiry.kind}_lead`] ?? humanize(inquiry.kind),
      person: nameFor(inquiry.firstName, inquiry.lastName, inquiry.email),
      email: inquiry.email,
      phone: inquiry.phone,
      business: inquiry.businessName,
      createdAt: inquiry.createdAt,
      status: inquiry.status,
      message: inquiry.message,
      details: {
        services: inquiry.services,
        decisionMaker: inquiry.isDecisionMaker ? "Yes" : "No",
        preferredTimeOne: inquiry.preferredDateOne,
        preferredTimeTwo: inquiry.preferredDateTwo,
      },
    }));
    const blueprints = dashboard.data.blueprintOrders.map(order => ({
      key: `blueprint-${order.id}`,
      category: "blueprint" as const,
      source: "blueprint" as const,
      id: order.id,
      label: order.kind === "paid" ? `${humanize(order.tier)} Blueprint order` : "Free Blueprint request",
      person: order.customerName || order.email,
      email: order.email,
      business: order.businessName,
      createdAt: order.createdAt,
      status: order.fulfillmentStatus,
      paymentStatus: order.paymentStatus,
      amountCents: order.amountCents,
      reference: order.reference,
      details: parseDetails(order.intake),
    }));
    const assessments = dashboard.data.assessmentAppointments.map(appointment => ({
      key: `assessment-${appointment.id}`,
      category: "assessment" as const,
      source: "assessment" as const,
      id: appointment.id,
      label: "Free fitness assessment",
      person: nameFor(appointment.firstName, appointment.lastName, appointment.email),
      email: appointment.email,
      phone: appointment.phone,
      createdAt: appointment.createdAt,
      status: appointment.status,
      reference: appointment.reference,
      details: {
        goal: appointment.primaryGoal,
        format: appointment.appointmentType === "virtual" ? "Virtual via Teams" : "In-person in NYC",
        scheduledFor: appointment.scheduledStart.toLocaleString(),
        outlookEventId: appointment.outlookEventId,
      },
    }));
    return [...currentForms, ...legacyLeads, ...assessments, ...blueprints].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [dashboard.data]);

  const counts = useMemo(() => ({
    leads: records.filter(record => record.category === "lead").length,
    newLeads: records.filter(record => record.category === "lead" && record.status === "new").length,
    blueprints: records.filter(record => record.category === "blueprint").length,
    activeBlueprints: records.filter(record => record.category === "blueprint" && ["queued", "awaiting_payment", "in_progress"].includes(record.status)).length,
    assessments: records.filter(record => record.category === "assessment").length,
    upcomingAssessments: records.filter(record => record.category === "assessment" && record.status === "confirmed").length,
  }), [records]);

  const availableStatuses = tab === "leads" ? leadStatuses : tab === "assessments" ? assessmentStatuses : tab === "blueprints" ? blueprintStatuses : [...leadStatuses, ...assessmentStatuses, ...blueprintStatuses];
  const filteredRecords = records.filter(record => {
    if (tab === "leads" && record.category !== "lead") return false;
    if (tab === "assessments" && record.category !== "assessment") return false;
    if (tab === "blueprints" && record.category !== "blueprint") return false;
    if (statusFilter !== "all" && record.status !== statusFilter) return false;
    const haystack = [record.person, record.email, record.business, record.label, record.reference].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  const updateStatus = (item: DashboardItem, status: string) => {
    if (item.category === "lead") {
      leadStatusMutation.mutate({ source: item.source as LeadSource, id: item.id, status: status as LeadStatus });
    } else if (item.category === "assessment") {
      assessmentStatusMutation.mutate({ id: item.id, status: status as AssessmentStatus });
    } else {
      blueprintStatusMutation.mutate({ id: item.id, status: status as BlueprintStatus });
    }
  };

  if (loading) return <DashboardLayout menuItems={navItems} title="Altitude Operations"><div className="ops-loading">Loading the secure operations workspace…</div></DashboardLayout>;
  if (!user) return <DashboardLayout menuItems={navItems} title="Altitude Operations"><div /></DashboardLayout>;
  if (user.role !== "admin") return <DashboardLayout menuItems={navItems} title="Altitude Operations"><section className="ops-access"><LockKeyhole size={28} /><p className="ops-overline">Private workspace</p><h1>Administrator access is required.</h1><p>This dashboard contains customer contact details and fulfillment data.</p></section></DashboardLayout>;

  return <DashboardLayout menuItems={navItems} title="Altitude Operations"><main className="ops-dashboard">
    <header className="ops-header"><div><p className="ops-overline">Altitude Fitness Management Group</p><h1>Operations dashboard</h1><p>Your central inbox for public leads, fitness assessments, applications, and Business Growth Blueprint requests.</p></div><button className="button button--navy" onClick={() => dashboard.refetch()} disabled={dashboard.isFetching}><RefreshCw size={16} className={dashboard.isFetching ? "spin" : ""} />Refresh data</button></header>
    <section className="ops-stat-grid" aria-label="Operations overview"><article><span className="ops-stat-icon"><UsersRound size={18} /></span><p>Captured leads</p><strong>{counts.leads}</strong><small>{counts.newLeads} new to review</small></article><article><span className="ops-stat-icon"><CalendarCheck2 size={18} /></span><p>Assessments</p><strong>{counts.assessments}</strong><small>{counts.upcomingAssessments} confirmed</small></article><article><span className="ops-stat-icon"><ClipboardList size={18} /></span><p>Blueprint requests</p><strong>{counts.blueprints}</strong><small>{counts.activeBlueprints} in progress</small></article></section>
    <section className="ops-workspace" id="lead-inbox"><div className="ops-workspace__head"><div><p className="ops-overline">Lead & fulfillment inbox</p><h2>Everything captured by the site.</h2></div><div className="ops-tabs" aria-label="Record category filters">{(["all", "leads", "assessments", "blueprints"] as Tab[]).map(value => <button key={value} className={tab === value ? "is-active" : ""} onClick={() => { setTab(value); setStatusFilter("all"); }}>{value === "all" ? `All (${records.length})` : value === "leads" ? `Leads (${counts.leads})` : value === "assessments" ? `Assessments (${counts.assessments})` : `Blueprints (${counts.blueprints})`}</button>)}</div></div>
    <div className="ops-filter-row"><label className="ops-search"><Search size={17} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search name, email, business, or reference" /></label><label className="ops-status-filter"><SlidersHorizontal size={16} /><span>Status</span><select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}><option value="all">All statuses</option>{availableStatuses.map(status => <option value={status} key={status}>{humanize(status)}</option>)}</select></label></div>
    {dashboard.isLoading ? <div className="ops-empty">Loading captured records…</div> : dashboard.error ? <div className="ops-empty">The inbox could not be loaded. Use Refresh data to try again.</div> : filteredRecords.length === 0 ? <div className="ops-empty"><FileText size={21} />No records match the current view.</div> : <div className="ops-record-table"><div className="ops-record-table__head"><span>Contact</span><span>Request</span><span>Status</span><span>Received</span><span /></div>{filteredRecords.map(record => <article className="ops-record" key={record.key}><button className="ops-record__main" onClick={() => setSelected(record)}><span className={`ops-record__type ops-record__type--${record.category}`}>{record.category === "blueprint" ? <ClipboardList size={17} /> : record.category === "assessment" ? <CalendarCheck2 size={17} /> : <UsersRound size={17} />}</span><span><strong>{record.person}</strong><small>{record.email}{record.business ? ` · ${record.business}` : ""}</small></span></button><button className="ops-record__request" onClick={() => setSelected(record)}><span>{record.label}</span>{record.reference && <small>{record.reference}</small>}</button><label className="ops-record__status"><StatusPill status={record.status} /><select aria-label={`Update status for ${record.person}`} value={record.status} disabled={leadStatusMutation.isPending || blueprintStatusMutation.isPending || assessmentStatusMutation.isPending} onChange={event => updateStatus(record, event.target.value)}>{(record.category === "lead" ? leadStatuses : record.category === "assessment" ? assessmentStatuses : blueprintStatuses).map(status => <option value={status} key={status}>{humanize(status)}</option>)}</select></label><button className="ops-record__date" onClick={() => setSelected(record)}>{record.createdAt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}<small>{record.createdAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</small></button><button className="ops-record__open" onClick={() => setSelected(record)} aria-label={`Open ${record.person}`}><ChevronRight size={18} /></button></article>)}</div>}
    </section><section className="ops-blueprint-strip" id="assessment-requests"><CalendarCheck2 size={22} /><div><strong>Fitness assessments</strong><span>Use the Assessments tab to review confirmed Outlook bookings, client goals, and format, then update each appointment as it is completed or canceled.</span></div><button onClick={() => { setTab("assessments"); document.querySelector("#lead-inbox")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>View assessments <ArrowUpRight size={15} /></button></section><section className="ops-blueprint-strip" id="blueprint-requests"><ClipboardList size={22} /><div><strong>Blueprint fulfillment</strong><span>Use the Blueprints tab to filter every free or paid Business Growth Blueprint request, then change its fulfillment status directly from the inbox.</span></div><button onClick={() => { setTab("blueprints"); document.querySelector("#lead-inbox")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}>View Blueprints <ArrowUpRight size={15} /></button></section>
  </main>{selected && <DetailsPanel item={selected} onClose={() => setSelected(null)} onStatusChange={status => updateStatus(selected, status)} updating={leadStatusMutation.isPending || blueprintStatusMutation.isPending || assessmentStatusMutation.isPending} />}</DashboardLayout>;
}
